import {
  Callout,
  DirectionalHint,
  Icon,
  Modal,
  PrimaryButton,
  DefaultButton,
  ProgressIndicator,
  Stack,
  Text,
  IDropdownOption,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import clsx from 'clsx';
import { AccountingDetails } from 'common/components/AccountingDetails';
import { CloseButton } from 'common/components/Buttons/CloseButton';
import { DocumentEntityList } from 'common/components/DocumentEntityList';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { DOCUMENT_INITIAL_VALUES } from '../constant';
import { validationSchema } from '../documentValidations';
import { EntityDocumentValues } from '../types';
import { CustomDropDown } from '../MyDocumentsView/CustomDropDown';
import { FormView } from '../MyDocumentsView/FormView';
import { DocumentTypeOptions } from 'common/types/utility';
import { useStyles } from './upload.styles';
import { PersonalDocuments_entityDocumentPersonalPools_nodes } from '../MyDocumentsList/__generated__/PersonalDocuments';

export interface UploadDocumentFormModalProps {
  visible: boolean;
  onDismiss: () => void;
  title: string;
  subtitle?: string;
  documentOptions?: DocumentTypeOptions[];
  companyCurrencies?: IDropdownOption[];
  documentData?: PersonalDocuments_entityDocumentPersonalPools_nodes | null;
  defaultDocumentTypeId?: number;
  isDocumentProtected?: boolean;
  isEdit: boolean;
  loading?: boolean;
  onSubmit: (
    values: EntityDocumentValues,
    fileSelected: File[]
  ) => Promise<void> | void;
  renderExtraFooterActions?: () => React.ReactNode;
  renderHeaderExtra?: () => React.ReactNode;
}

export const UploadDocumentFormModal: React.FC<UploadDocumentFormModalProps> = ({
  visible,
  onDismiss,
  title,
  subtitle,
  documentOptions,
  companyCurrencies,
  documentData,
  defaultDocumentTypeId,
  isDocumentProtected = false,
  isEdit,
  loading,
  onSubmit,
  renderExtraFooterActions,
  renderHeaderExtra,
}) => {
  const styles = useStyles();

  const [fileSelected, setFileSelected] = useState<File[]>([]);
  const [isAccountingDocument, setIsAccountingDocument] =
    useState<boolean>(false);

  const [dataExtractModelOptions, setDataExtractModelOptions] = useState<
    IDropdownOption[]
  >([]);
  const [hideExtractionDropdown, setHideExtractionDropdown] =
    useState<boolean>(false);
  const [documentType, setDocumentType] = useState<DocumentTypeOptions>();
  const [isDisabled, setIsDisabled] = useState(false);

  const onDrop = (files: File[]) => {
    setFileSelected((prev) => [...prev, ...files]);
  };

  const multipleIsAccountingDocumentSelected =
    isAccountingDocument && fileSelected.length > 1;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noDragEventsBubbling: true,
    multiple: true,
  });

  const onRemoveFile = (fileIndex: number) => {
    setFileSelected((prev) => prev.filter((_, index) => fileIndex !== index));
  };
  //  useEffect(() => {
  //    if (documentData?.id) {
  //      setIsAccountingDocument(
  //        documentData.documentTypes?.isAccountingDocument || false
  //      );
  //      const matchedType = documentOptions?.find(
  //        (opt) => opt.key === documentData.entityDocumentTypeId
  //      );
  //      if (matchedType) setDocumentType(matchedType);
  //    }
  //  }, [documentData, documentOptions]);

  useEffect(() => {
    if (defaultDocumentTypeId) {
      const optionMap = documentOptions?.map(
        (item) => item as DocumentTypeOptions
      );
      let optionFilter: DocumentTypeOptions[] | undefined;
      if (documentData && documentData.id) {
        optionFilter = optionMap?.filter(
          (item) => item.key === documentData?.entityDocumentTypeId
        );
      } else {
        optionFilter = optionMap?.filter(
          (item) => item.key === defaultDocumentTypeId
        );
      }
      if (optionFilter) {
        setDocumentType(optionFilter[0]);
        setIsAccountingDocument(optionFilter[0]?.isAccountingDocument || false);
        if (optionFilter[0]?.isAccountingDocument) {
          setFileSelected([]);
        }
      }
    }
  }, [visible, defaultDocumentTypeId, documentOptions, documentData]);

  const initialValues: EntityDocumentValues = documentData
    ? {
      ...documentData,
      indexTransactionDate: documentData.indexTransactionDate
        ? documentData.indexTransactionDate
        : null,
    }
    : DOCUMENT_INITIAL_VALUES;

  const clearData = () => {
    setIsAccountingDocument(false);
    setDocumentType(undefined);
    setFileSelected([]);
  };


  return (
    <>
      <Formik<EntityDocumentValues>
        enableReinitialize
        initialValues={initialValues}
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={(values) => onSubmit(values, fileSelected)}
      >
        {({ submitForm, isSubmitting, dirty, resetForm, errors, values }) => {
          return (
            <Form>
              <Modal isOpen={visible} isBlocking={true} onDismiss={onDismiss} styles={{
                main: {
                  minWidth: 600,
                },
              }}>
                <Stack>
                  <Stack
                    horizontal
                    horizontalAlign={'space-between'}
                    className={styles.dialogStyles}
                  >
                    <Stack tokens={{ childrenGap: 5 }}>
                      <Text variant={'xLarge'}>{title}</Text>
                      {subtitle && <Text variant={'medium'}>{subtitle}</Text>}
                      {renderHeaderExtra?.()}
                    </Stack>
                    <CloseButton
                      onClick={() => {
                        resetForm();
                        clearData();
                        onDismiss();
                      }}
                    />
                  </Stack>
                  <Stack
                    tokens={{ childrenGap: 20 }}
                    className={styles.container}
                    styles={{
                      root: {
                        marginTop: '90px',
                      },
                    }}
                  >
                    {documentOptions && (
                      <CustomDropDown
                        isEdit={isEdit}
                        defaultDocumentTypeId={defaultDocumentTypeId}
                        documentData={documentData || null}
                        isDocumentProtected={isDocumentProtected}
                        documentOptions={documentOptions}
                        setDocumentType={(data) => setDocumentType(data)}
                        setFileSelected={() => setFileSelected([])}
                        setIsAccountingDocument={(isAccounting) =>
                          setIsAccountingDocument(isAccounting!)
                        }
                        disabled={isDisabled}
                        onDocumentTypeChange={setDataExtractModelOptions}
                        hideExtractionDropdown={() =>
                          setHideExtractionDropdown(true)
                        }
                      />
                    )}

                    {!isEdit && values.entityDocumentTypeId && (
                      <div
                        {...getRootProps()}
                        className={clsx(
                          styles.dropZone,
                          isDragActive && styles.dropZoneHover
                        )}
                      >
                        <Stack
                          grow
                          horizontalAlign="center"
                          verticalAlign="center"
                          verticalFill
                        >
                          <input {...getInputProps({ multiple: true })} />
                          {isDragActive ? (
                            <Stack horizontal tokens={{ childrenGap: 20 }}>
                              <Icon
                                iconName={'CloudUpload'}
                                className={styles.uploadIconStyle}
                              />
                              <Text className={styles.uploadIconStyle}>
                                Drop the files here...
                              </Text>
                            </Stack>
                          ) : (
                            <Text>
                              Drag 'n' drop some files here, or click to select
                              files
                            </Text>
                          )}
                        </Stack>
                      </div>
                    )}

                    {fileSelected.length > 0 && (
                      <DocumentEntityList
                        onRemoveFile={onRemoveFile}
                        filesList={fileSelected!}
                      />
                    )}

                    <FormView
                      isAccountingDocument={isAccountingDocument}
                      isDocumentProtected={isDocumentProtected}
                      showIsAccountingDocumentInputs={
                        !multipleIsAccountingDocumentSelected
                      }
                      documentData={documentData}
                      documentType={documentType}
                      companyCurrencies={companyCurrencies}
                      onInputFieldsDisabled={setIsDisabled}
                      dataExtractModelOptions={dataExtractModelOptions}
                      isEdit={isEdit}
                      hideExtractionDropdown={hideExtractionDropdown}
                    />
                  </Stack>

                  <Stack className={styles.footerStyles}>
                    {loading && <ProgressIndicator />}
                    <Stack
                      horizontalAlign="space-between"
                      horizontal
                      tokens={{ childrenGap: 6, padding: 25 }}
                    >
                      <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <PrimaryButton
                          disabled={
                            !dirty ||
                            Object.keys(errors).length > 0 ||
                            isSubmitting ||
                            (!documentData?.id && fileSelected.length === 0) ||
                            documentType === undefined
                          }
                          text={isEdit ? 'Update' : 'Upload'}
                          onClick={async () => {
                            await submitForm();
                            resetForm();
                            clearData();
                            onDismiss();
                          }}
                        />
                        <DefaultButton
                          onClick={() => {
                            resetForm();
                            clearData();
                            onDismiss();
                          }}
                          text="Cancel"
                        />
                      </Stack>
                      <Stack>{renderExtraFooterActions?.()}</Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Modal>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export { Callout, DirectionalHint, AccountingDetails, useId };